"use client";

import { ScriptBlockKind } from "@prisma/client";
import { useState } from "react";
import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import ScriptCallItems from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptCallItems/ScriptCallItems";
import ScriptItemList from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptItemList/ScriptItemList";
import ScriptSuggestions from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptSuggestions/ScriptSuggestions";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import {
  ScriptBlockData,
  ScriptItemInput,
  ScriptSourceItem,
} from "@/app/types/messages-content/ScriptData";
import { Limits } from "@/app/vars/Limits";
import { buildScriptPlaceholderRows } from "../../Scripts.logic";
import {
  blockAddLabel,
  blockCallsTitle,
  blockItemPlaceholder,
  blockPlaceholder,
  blockPlaceholderRowCount,
  blockResponsePlaceholder,
  blockShape,
  blockSuggestionTitle,
  blockTitle,
} from "./ScriptBlock.logic";
import styles from "./ScriptBlock.module.css";

type Props = {
  block: ScriptBlockData;
  suggestions: ScriptSourceItem[];
  isCallMode: boolean;
  isPlaceholder: boolean;
  onOpenQuotes: (insightId: number, label: string) => void;
  onSaveBody?: (body: string) => void;
  onSaveItems?: (items: ScriptItemInput[]) => void;
  onSaveResponse?: (label: string, response: string) => void;
  onSaveForInsight?: (insightId: number, label: string) => void;
};

export default function ScriptBlock(props: Props) {
  const T = useT();
  const {
    block,
    suggestions,
    isCallMode,
    isPlaceholder,
    onOpenQuotes,
    onSaveBody,
    onSaveItems,
    onSaveResponse,
    onSaveForInsight,
  } = props;

  const [filter, setFilter] = useState({ query: "" });

  const shape = blockShape(block.type);
  const showFilter = isCallMode && !!shape?.isFilterable;

  const fromCalls = block.items.filter((item) => item.sourceInsightId !== null);
  const manual = block.items.filter((item) => item.sourceInsightId === null);
  const listItems = isCallMode || !shape?.source ? block.items : manual;

  return (
    <div className={styles.block}>
      <h3 className={styles.title}>{blockTitle(T, block.type)}</h3>

      {shape?.suggestion && !isCallMode && (
        <ScriptSuggestions
          blockType={block.type}
          title={blockSuggestionTitle(T, block.type)}
          items={suggestions}
          isPlaceholder={isPlaceholder}
          answers={block.items}
          answerPlaceholder={T(TKey.MESSAGES_CONTENT_SCRIPT_PAIN_QUESTION_PLACEHOLDER) as string}
          onOpenQuotes={onOpenQuotes}
          onSaveAnswer={onSaveForInsight}
        />
      )}

      {!!shape?.source && !isCallMode && (
        <ScriptCallItems
          blockType={block.type}
          title={blockCallsTitle(T, block.type)}
          items={fromCalls}
          isPlaceholder={isPlaceholder}
          responsePlaceholder={blockResponsePlaceholder(T, block.type)}
          onOpenQuotes={onOpenQuotes}
          onSaveResponse={shape.hasResponse ? onSaveResponse : undefined}
        />
      )}

      {showFilter && (
        <Input
          className={styles.filterInput}
          type={TextInputType.TEXT}
          data={filter}
          dataKey="query"
          placeholder={T(TKey.MESSAGES_CONTENT_SCRIPT_FILTER_PLACEHOLDER) as string}
          onChange={(data) => setFilter({ query: data.query })}
        />
      )}

      <div className={styles.body}>
        {isPlaceholder && block.kind === ScriptBlockKind.TEXT ? (
          <BlurredPlaceholder
            rows={buildScriptPlaceholderRows(blockPlaceholderRowCount(block.type))}
          />
        ) : block.kind === ScriptBlockKind.TEXT ? (
          <EditableText
            value={block.body ?? ""}
            onSave={(value) => onSaveBody?.(value)}
            placeholder={isCallMode ? "" : blockPlaceholder(T, block.type)}
            multiline={true}
            textClassName={styles.text}
            inputClassName={styles.text}
            maxLength={Limits.MAX_SCRIPT_BLOCK_BODY}
            disabled={!onSaveBody}
          />
        ) : (
          <ScriptItemList
            items={listItems}
            maxItems={shape?.maxItems ?? 0}
            addLabel={blockAddLabel(T, block.type)}
            placeholder={blockItemPlaceholder(T, block.type)}
            responsePlaceholder={blockResponsePlaceholder(T, block.type)}
            isCallMode={isCallMode}
            isOrdered={!!shape?.isOrdered}
            isPlaceholder={isPlaceholder}
            placeholderRows={blockPlaceholderRowCount(block.type)}
            hasResponse={!!shape?.hasResponse}
            filter={showFilter ? filter.query : ""}
            onSave={onSaveItems}
            onSaveResponse={shape?.hasResponse ? onSaveResponse : undefined}
          />
        )}
      </div>
    </div>
  );
}
